// Geocoding utilities for zip code and city name to coordinates conversion
// - Zip codes: Zippopotam.us (no API key required)
// - City typeahead: local /api/cities route backed by bundled cities.json dataset
//   (instant, deterministic, no external API, no rate limits)
// - City direct-geocode fallback: Nominatim/OpenStreetMap

export interface GeocodingResult {
  latitude: number
  longitude: number
  city: string
  state: string
  country: string
}

// ─── Zip code geocoding ───────────────────────────────────────────────────────

// Convert US zip code to coordinates using Zippopotam.us
export async function geocodeZipCode(zipCode: string): Promise<GeocodingResult | null> {
  const cleanZip = zipCode.trim()
  if (!/^\d{5}$/.test(cleanZip)) {
    console.error('[Geocoding] Invalid zip code format:', cleanZip)
    return null
  }

  try {
    const response = await fetch(`https://api.zippopotam.us/us/${cleanZip}`)

    if (!response.ok) {
      if (response.status === 404) {
        console.error('[Geocoding] Zip code not found:', cleanZip)
        return null
      }
      throw new Error(`Geocoding API error: ${response.status}`)
    }

    const data = await response.json()

    if (!data.places || data.places.length === 0) {
      console.error('[Geocoding] No places found for zip:', cleanZip)
      return null
    }

    const place = data.places[0]

    return {
      latitude: parseFloat(place.latitude),
      longitude: parseFloat(place.longitude),
      city: place['place name'],
      state: place['state abbreviation'],
      country: data['country abbreviation'],
    }
  } catch (error) {
    console.error('[Geocoding] Error geocoding zip code:', error)
    return null
  }
}

// Validate zip code format without making API call
export function isValidZipCode(zipCode: string): boolean {
  return /^\d{5}$/.test(zipCode.trim())
}

// Returns true if the input looks like a city (or city, state) rather than a zip code
export function looksLikeCity(input: string): boolean {
  return !/^\d+$/.test(input.trim())
}

// ─── Typeahead ────────────────────────────────────────────────────────────────

export interface Citysuggestion {
  label: string // e.g. "San Francisco, CA"
  result: GeocodingResult
}

/**
 * Search for up to `limit` US city suggestions matching `query`.
 *
 * Calls the local /api/cities route which loads cities.json once server-side
 * and performs instant prefix matching — no external API, no rate limits.
 */
export async function searchCities(
  query: string,
  limit = 5
): Promise<Citysuggestion[]> {
  const q = query.trim()
  if (q.length < 2) return []

  try {
    const params = new URLSearchParams({ q, limit: String(limit) })
    const response = await fetch(`/api/cities?${params}`)
    if (!response.ok) return []

    const results = await response.json()
    if (!Array.isArray(results)) return []

    return results.map((r: { label: string; city: string; state: string; latitude: number; longitude: number }) => ({
      label: r.label,
      result: {
        latitude: r.latitude,
        longitude: r.longitude,
        city: r.city,
        state: r.state,
        country: 'US',
      },
    }))
  } catch {
    return []
  }
}

// ─── Direct geocode (used when submitting without picking a suggestion) ───────

// Geocode a city name (optionally "City, ST") using Nominatim (OpenStreetMap)
export async function geocodeCity(city: string): Promise<GeocodingResult | null> {
  const query = city.trim()
  if (!query) return null

  try {
    const params = new URLSearchParams({
      q: query,
      countrycodes: 'us',
      format: 'json',
      limit: '1',
      addressdetails: '1',
    })
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?${params}`,
      {
        headers: {
          'User-Agent': 'AMCShowtimesFinder/1.0',
          'Accept-Language': 'en',
        },
      }
    )

    if (!response.ok) {
      throw new Error(`Nominatim API error: ${response.status}`)
    }

    const results = await response.json()
    if (!results || results.length === 0) return null

    const place = results[0]
    const address = place.address || {}
    const cityName =
      address.city || address.town || address.village || address.county || query
    const state = address.state_code || address.state || ''

    return {
      latitude: parseFloat(place.lat),
      longitude: parseFloat(place.lon),
      city: cityName,
      state,
      country: 'US',
    }
  } catch (error) {
    console.error('[Geocoding] Error geocoding city:', error)
    return null
  }
}

// ─── Reverse geocode (lat/lng → city name) ────────────────────────────────────

// Given coordinates from browser geolocation, return a human-readable "City, ST" label.
// Uses Nominatim reverse geocoding — free, no API key, called once per user action.
export async function reverseGeocode(latitude: number, longitude: number): Promise<string | null> {
  try {
    const params = new URLSearchParams({
      lat: String(latitude),
      lon: String(longitude),
      format: 'json',
      addressdetails: '1',
    })
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?${params}`,
      {
        headers: {
          'User-Agent': 'AMCShowtimesFinder/1.0',
          'Accept-Language': 'en',
        },
      }
    )

    if (!response.ok) return null

    const data = await response.json()
    const address = data.address || {}
    const city =
      address.city || address.town || address.village ||
      address.county || address.state_district || ''
    const state = address.state_code || address.state || ''

    if (city && state) return `${city}, ${state}`
    if (city) return city
    return null
  } catch (error) {
    console.error('[Geocoding] Error reverse geocoding:', error)
    return null
  }
}
